import {
  type ActionOutputData,
  type GetActionTestResponse,
  type InputConfig,
  type OutputData,
  unwrapValueConfig,
  type ValueConfig,
  type ValueData,
} from "@smartsuite/automation-engine-api";
import { isTruthy } from "remeda";
import { useTranslation } from "react-i18next";
import { translateIfPresent } from "../../../../../../locales/i18n";

interface ResponseDetailHook {
  outputs: ResponseOutputData[];
  inputData: InputData[];
}

interface InputData {
  id: string;
  label: string;
  value: string;
}

interface ResponseOutputData {
  outputId: string;
  outputLabel: string;
  value: string;
  icon?: string;
}

const INPUTS_TO_INPUT_BY_TYPE: string[] = ["response-content"];

// we need to format the output data to be displayed in the response details
// example of the output data:
// Headers {"auth": "test ", "some": "test"}
const getInputData = (
  outputs: OutputData[],
  id: string,
  label = "",
  value?: ValueConfig,
): InputData | undefined => {
  if (value === undefined) {
    return undefined;
  }
  value = unwrapValueConfig(value);
  return value.values.reduce(
    (acc, v, i) => {
      let val = "";
      let startObject = false;
      let endObject = false;
      switch (v.value.oneofKind) {
        case "string":
          val = INPUTS_TO_INPUT_BY_TYPE.includes(id)
            ? translateIfPresent("caption.Text", "Text")
            : v.value.string;
          break;
        case "number":
          val = INPUTS_TO_INPUT_BY_TYPE.includes(id)
            ? translateIfPresent("caption.Number", "Number")
            : v.value.number.toString();
          break;
        case "bool":
          val = INPUTS_TO_INPUT_BY_TYPE.includes(id)
            ? translateIfPresent("caption.Boolean", "Boolean")
            : v.value.bool
              ? "true"
              : "false";
          break;
        case "reference": {
          const output = outputs.find(
            (output) =>
              output.outputId ===
              (v.value.oneofKind === "reference"
                ? v.value.reference.outputId
                : ""),
          );
          val =
            output?.value?.values
              .map((v) =>
                v.value.oneofKind === "string" ? v.value.string : "",
              )
              .join("") ?? "";
          break;
        }
        case "keyValue": {
          startObject = i === 0;
          endObject = i === (value?.values?.length ?? 0) - 1;
          const key =
            v.value.keyValue.key?.values[0]?.value?.oneofKind === "string"
              ? v.value.keyValue.key?.values[0].value.string
              : "";
          let values =
            getInputData(outputs, id, key, v.value.keyValue.value)?.value ?? "";
          values =
            values.startsWith("{") || INPUTS_TO_INPUT_BY_TYPE.includes(id)
              ? values
              : `"${values}"`;
          val = `"${key}": ${values}${endObject ? "" : ", "}`;
          break;
        }
        case "list": {
          const items = v.value.list.values
            .map((v) => getInputData(outputs, id, label, v)?.value ?? "")
            .join(", ");
          val = `[${items}]`;
          break;
        }
      }
      return {
        ...acc,
        value: `${startObject ? "{" : ""}${acc.value.concat(val)}${
          endObject ? "}" : ""
        }`,
      };
    },
    { id, label, value: "" },
  );
};

const getInputDatas = (
  inputs: InputConfig[],
  outputs: OutputData[],
): InputData[] => {
  return inputs
    .flatMap((input) => {
      return getInputData(outputs, input.inputId, input.label, input.value);
    })
    .filter(isTruthy);
};

const getOutputData = (valueData?: ValueData): string => {
  if (valueData === undefined) {
    return "";
  }
  let startObject = false;
  let endObject = false;
  return valueData.values
    .flatMap((valueV, i) => {
      if (valueV.value.oneofKind === "string") {
        return valueV.value.string;
      } else if (valueV.value.oneofKind === "number") {
        return valueV.value.number.toString();
      } else if (valueV.value.oneofKind === "bool") {
        return valueV.value.bool ? "true" : "false";
      } else if (valueV.value.oneofKind === "list") {
        const items = valueV.value.list.values
          .map((v) => getOutputData(v))
          .join(", ");
        return `[${items}]`;
      } else if (valueV.value.oneofKind === "keyValue") {
        startObject = i === 0;
        endObject = i === (valueData.values.length ?? 0) - 1;
        const key =
          valueV.value.keyValue.key?.values[0]?.value?.oneofKind === "string"
            ? valueV.value.keyValue.key?.values[0].value.string
            : "";
        let value = getOutputData(valueV.value.keyValue.value);
        value = value.startsWith("{") ? value : `"${value}"`;
        return `${startObject ? "{" : ""}"${key}": ${value}${
          endObject ? "}" : ""
        }`;
      }
      return "";
    })
    .join(", ");
};

export const useResponseDetail = (
  actionTestResponse: GetActionTestResponse,
): ResponseDetailHook => {
  const { t } = useTranslation();
  const actionOutputData: ActionOutputData | undefined =
    actionTestResponse?.actionTest?.status.oneofKind === "complete"
      ? actionTestResponse.actionTest.status.complete.actionOutputs
      : undefined;

  const outputs: ResponseOutputData[] = (actionOutputData?.outputs ?? []).map(
    (output) => {
      const value = getOutputData(output.value);

      return {
        outputId: output.outputId,
        outputLabel: output.outputLabel,
        icon: output.icon,
        value: value.length > 0 ? value : t("general.no-value"),
      };
    },
  );
  const inputs: InputConfig[] =
    actionTestResponse.actionTest?.action?.inputs ?? [];
  const outputFotInputs: OutputData[] =
    actionTestResponse.actionTest?.testInputs ?? [];

  const inputData = getInputDatas(inputs, outputFotInputs);
  return { outputs, inputData };
};
